/* =========================================================================
   YTHRAL — theme.js
   ========================================================================= */
(function () {
  'use strict';

  // ---- Loader — shows ONLY on first visit + logo-click-to-home ----
  const loader = document.getElementById('ritual-loader');
  if (loader) {
    let duration = parseInt(loader.getAttribute('data-duration'), 10) || 800;
    // Mobile: cap loader duration — simplified animations finish in ~2s,
    // no point running GPU-heavy effects for 6+ seconds during page load
    if (window.innerWidth <= 900 && duration > 2500) duration = 2500;
    
    const SESSION_KEY = 'ythral_loader_seen';
    const LOGO_KEY    = 'ythral_loader_logo';

    const stopLoaderAnimations = () => {
      // Kill all running animations inside the loader once it's gone —
      // some browsers continue running CSS animations on opacity:0 elements
      loader.querySelectorAll('*').forEach(el => {
        el.style.animationPlayState = 'paused';
      });
    };
    // hideNow: INSTANT hide — no transition, no flash, no partial blood fill.
    // Used for non-first-visit pages where the loader shouldn't be seen at all.
    const hideNow = () => {
      loader.style.transition = 'none';
      loader.style.opacity = '0';
      loader.style.visibility = 'hidden';
      loader.style.pointerEvents = 'none';
      loader.classList.add('gone');
      stopLoaderAnimations();
    };
    // hideAfter: cinematic fade-out after the full animation plays.
    // Used for first visit + logo-to-home (intentional loader experience).
    const hideAfter = () => setTimeout(() => {
      loader.classList.add('gone');
      stopLoaderAnimations();
    }, duration);

    // Was this page triggered by a logo-click?
    const logoNav = sessionStorage.getItem(LOGO_KEY) === '1';
    if (logoNav) sessionStorage.removeItem(LOGO_KEY);

    // First ever visit in this browser session OR logo-click navigation
    const isFirstVisit = !sessionStorage.getItem(SESSION_KEY);
    if (isFirstVisit) sessionStorage.setItem(SESSION_KEY, '1');

    if (isFirstVisit || logoNav) {
      // Let the loader play its full animation, then fade
      if (document.readyState === 'complete') hideAfter();
      else window.addEventListener('load', hideAfter);
    } else {
      // All other page loads: INSTANT hide — no animation flash
      hideNow();
    }

    // Logo / brand link → trigger loader on next page (home navigation)
    document.addEventListener('DOMContentLoaded', () => {
      const logoLinks = document.querySelectorAll(
        'nav.top .brand, a.hdr-brand, a[href="{{ routes.root_url }}"], a[href="/"]'
      );
      logoLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          // Only intercept if not already on the home page
          if (window.location.pathname !== '/' && window.location.pathname !== '') {
            sessionStorage.setItem(LOGO_KEY, '1');
            // Let the browser navigate naturally (no preventDefault needed)
          }
        });
      });
    });
  }

  // ---- Blood Cursor ----
  const cursor = document.getElementById('blood-cursor');
  if (cursor && window.matchMedia('(pointer: fine)').matches) {
    document.addEventListener('mousemove', (e) => {
      cursor.style.left = `${e.clientX}px`;
      cursor.style.top = `${e.clientY}px`;
      if (Math.random() > 0.92) {
        const drop = document.createElement('div');
        drop.className = 'blood-drop';
        drop.style.left = `${e.clientX}px`;
        drop.style.top = `${e.clientY}px`;
        document.body.appendChild(drop);
        requestAnimationFrame(() => {
          drop.style.top = `${e.clientY + 60 + Math.random() * 40}px`;
          drop.style.opacity = '0';
        });
        setTimeout(() => drop.remove(), 1200);
      }
    }, { passive: true });
  }

  // ---- Parallax (hero figures) ----
  const parallaxElements = {
    figD: document.querySelector('.figure.desktop'),
    figM: document.querySelector('.figure.mobile'),
    floor: document.querySelector('.floor-sigil')
  };
  if (parallaxElements.figD || parallaxElements.figM || parallaxElements.floor) {
    let ticking = false;
    let parallaxVisible = true; // IntersectionObserver controls this
    function updateParallax() {
      if (!parallaxVisible) { ticking = false; return; }
      const p = Math.min(window.scrollY / window.innerHeight, 1);
      const t = `translateX(-50%) translateY(${p * -30}px) scale(${1 - p * 0.15})`;
      if (parallaxElements.figD) parallaxElements.figD.style.transform = t;
      if (parallaxElements.figM) parallaxElements.figM.style.transform = t;
      if (parallaxElements.floor) {
        parallaxElements.floor.style.transform = `translate(-50%,-50%) rotateX(${72 + p * 8}deg) scale(${1 + p * 0.25})`;
      }
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(updateParallax); ticking = true; }
    }, { passive: true });

    // Pause parallax when hero is off-screen — saves scroll-handler CPU
    const riteSection = document.querySelector('.rite');
    if (riteSection && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        parallaxVisible = entries[0].isIntersecting;
      }, { threshold: 0 }).observe(riteSection);
    }
  }

  // ============================================================
  // SCROLL REVEAL — Intersection Observer based, performant
  // Auto-applies to common section elements + product cards.
  // Merchant can override per-section by setting data-reveal manually.
  // ============================================================
  function initScrollReveal() {
    // Bail if disabled via body attribute (merchant toggle) or reduced-motion
    if (document.body.dataset.scrollReveal === 'off') return;
    if (!('IntersectionObserver' in window)) return;

    // Default reveal style is read from <body data-reveal-default="lift"> etc.
    const defaultStyle = document.body.dataset.revealDefault || 'rise';

    // Auto-tag elements that should reveal (only if they don't already have data-reveal)
    const autoSelectors = [
      '.shopify-section > section',          // every section's main element
      '.shopify-section > .page-wrap',
      '.card',                               // product cards
      '.altar .product-float',
      '.masks .col',
      '.fc-ed-content',
      '.fc-cr-content',
      '.communion .marquee',
      '.mp-inner > *',                       // page content blocks
      '.trk-card',                           // tracker cards
      '.ns-inner > *',                       // newsletter
      '.rt-inner > *'                        // rich text
    ];

    autoSelectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (!el.hasAttribute('data-reveal') && !el.closest('[data-reveal-stagger]')) {
          // For top-level cards, use a stagger on the parent grid instead
          el.setAttribute('data-reveal', defaultStyle === 'auto' ? 'rise' : defaultStyle);
        }
      });
    });

    // Stagger product grids — children animate one after another
    const staggerSelectors = ['.grid', '.fc-cr-track', '.fc-ed-grid', '.mlc-grid', '.mc-grid', '.ms-grid', '.mb-grid'];
    staggerSelectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (!el.hasAttribute('data-reveal-stagger')) {
          el.setAttribute('data-reveal-stagger', '');
          // Children inside a stagger don't need their own data-reveal
          el.querySelectorAll('[data-reveal]').forEach(c => c.removeAttribute('data-reveal'));
        }
      });
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.01,
      rootMargin: '0px 0px 0px 0px'
    });

    document.querySelectorAll('[data-reveal], [data-reveal-stagger]').forEach((el) => {
      // Immediately reveal elements already in the viewport on page load
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('is-in');
      } else {
        observer.observe(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScrollReveal);
  } else {
    initScrollReveal();
  }

  // Re-run when Shopify editor inserts/reloads sections
  document.addEventListener('shopify:section:load', initScrollReveal);

  // ---- Global cart count syncer ----
  // Call window.YthralUpdateCartCount(count) after any AJAX add/remove so
  // every cart UI (header bag, mobile tab bar badge, legacy .cart-link)
  // reflects the new count without a full page reload.
  window.YthralUpdateCartCount = function(count){
    var n = parseInt(count, 10);
    if (!isFinite(n) || n < 0) n = 0;

    // 1) Header bag count chip
    document.querySelectorAll('.hdr-cart-count').forEach(function(el){
      el.textContent = String(n);
    });
    document.querySelectorAll('.hdr-cart').forEach(function(a){
      a.setAttribute('aria-label', 'Cart, ' + n + ' items');
    });

    // 2) Mobile tab-bar cart badge (insert if missing)
    document.querySelectorAll('.mtb-tab[data-tab="cart"]').forEach(function(tab){
      var badge = tab.querySelector('.mtb-badge');
      if (n > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'mtb-badge';
          tab.appendChild(badge);
        }
        badge.textContent = String(n);
        badge.setAttribute('aria-label', n + ' items');
      } else if (badge) {
        badge.remove();
      }
    });

    // 3) Legacy .cart-link selectors (older theme code paths)
    document.querySelectorAll('.cart-link').forEach(function(el){
      if (/\(\d+\)/.test(el.textContent)) {
        el.textContent = el.textContent.replace(/\(\d+\)/, '(' + n + ')');
      }
    });
  };

  // ---- Lazy image load detection ----
  // Toggle .is-loaded class when images finish loading so the CSS shimmer
  // animation stops. The old img[src] selector never worked because Shopify
  // sets src immediately — images need a load event to know when they're done.
  function markLoaded(img) { img.classList.add('is-loaded'); }
  document.querySelectorAll('img[loading="lazy"]').forEach(function(img) {
    if (img.complete && img.naturalWidth > 0) {
      markLoaded(img);
    } else {
      img.addEventListener('load', function() { markLoaded(img); }, { once: true });
    }
  });

  // ---- Marquee visibility pause ----
  // Pause the infinite translateX animation when the marquee scrolls off-screen.
  // This prevents a large-element transform from running continuously off-screen.
  if ('IntersectionObserver' in window) {
    document.querySelectorAll('.marquee').forEach(function(m) {
      new IntersectionObserver(function(entries) {
        m.style.animationPlayState = entries[0].isIntersecting ? 'running' : 'paused';
      }, { threshold: 0 }).observe(m);
    });
  }
})();
